import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, History, RefreshCw, Camera } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const getInitials = (name) => {
  if (!name || name === 'Desconocido') return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const LiveMatchAccessScreen = () => {
  const { slug, id } = useParams();
  const [logs, setLogs] = useState([]);
  const [currentFlash, setCurrentFlash] = useState(null);
  const flashTimer = useRef(null);

  const fetchLogsRef = useRef(null);

  // Scanner State
  const [scanner, setScanner] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const isScanningRef = useRef(false);
  const localFlashActiveRef = useRef(false);
  const [scanLoading, setScanLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:11200';
  const lastLogIdRef = useRef(null);



  // KIOSK SCANNER LOGIC
  useEffect(() => {
    Html5Qrcode.getCameras().then(devices => {
      if (devices && devices.length > 0) {
        setCameras(devices);
        startScanner(devices[0].id);
      }
    }).catch(err => {
      console.error("Error obteniendo cámaras", err);
    });

    return () => {
      if (scanner && scanner.isScanning) {
        scanner.stop().catch(console.error);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanner = (cameraId) => {
    const html5QrCode = new Html5Qrcode("kiosk-reader");
    setScanner(html5QrCode);
    setIsScanning(true);
    isScanningRef.current = true;

    html5QrCode.start(
      cameraId,
      { fps: 10, qrbox: { width: 300, height: 300 } },
      (decodedText) => {
        handleScan(decodedText, html5QrCode);
      },
      (errorMessage) => {}
    ).catch(err => {
      console.error("Error iniciando scanner", err);
      setIsScanning(false);
    });
  };

  const stopScanner = async () => {
    if (scanner && scanner.isScanning) {
      try {
        await scanner.stop();
        setScanner(null);
        setIsScanning(false);
        isScanningRef.current = false;
      } catch (err) {
        console.error("Error deteniendo scanner", err);
      }
    }
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    await stopScanner();
    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(nextIndex);
    startScanner(cameras[nextIndex].id);
  };

  const handleScan = async (decodedText, scannerInstance) => {
    if (scanLoading) return;
    setScanLoading(true);

    let flashData = null;
    try {
      await scannerInstance.pause(false);
      setIsScanning(false); // Pausamos lógica visual
      isScanningRef.current = false;

      const response = await axios.post(`${apiUrl}/${slug}/api/match-tickets/validate`, { qrCode: decodedText }, {
        withCredentials: true,
      });
      
      // Armamos un objeto flash local instantáneo
      flashData = {
        status: response.data.valid ? 'granted' : 'denied',
        userName: response.data.ticket?.userName || 'Desconocido',
        ticketType: response.data.ticket?.ticketType || '',
        message: response.data.message
      };

      setCurrentFlash(flashData);
      localFlashActiveRef.current = true;
      

      // Add local log
      const newLog = {
        _id: Date.now().toString(),
        userName: flashData.userName,
        status: flashData.status,
        message: flashData.message,
        createdAt: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev].slice(0, 50));


    } catch (err) {
      // Error de validación (denied)
      flashData = {
        status: 'denied',
        userName: 'Desconocido',
        ticketType: '',
        message: err.response?.data?.message || "Error de validación"
      };
      setCurrentFlash(flashData);
      localFlashActiveRef.current = true;

      // Add local log
      const newLog = {
        _id: Date.now().toString(),
        userName: flashData.userName,
        status: flashData.status,
        message: flashData.message,
        createdAt: new Date().toISOString()
      };
      setLogs(prev => [newLog, ...prev].slice(0, 50));

    } finally {
      setScanLoading(false);
      
      const flashDuration = flashData?.status === 'denied' ? 6000 : 4000;
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => {
        setCurrentFlash(null);
        localFlashActiveRef.current = false;
        
        // Retomar escaneo después
        scannerInstance.resume();
        setIsScanning(true);
        isScanningRef.current = true;
      }, flashDuration);
    }
  };

  // Si hay flash, es porque acaba de entrar alguien o fue denegado.
  const isFlashActive = !!currentFlash;
  const isDenied = currentFlash?.status === 'denied';

  return (
    <div className="h-screen w-screen bg-black text-white overflow-hidden flex font-sans selection:bg-none">
      {/* 🔴 O 🟢 FLASH FULLSCREEN OVERLAY */}
      <div 
        className={`absolute inset-0 z-50 flex flex-col items-center justify-center transition-all duration-300 transform scale-100 ${
          isFlashActive 
            ? (isDenied ? 'bg-red-600/95 opacity-100' : 'bg-green-500/95 opacity-100')
            : 'opacity-0 pointer-events-none scale-105'
        }`}
      >
        {isFlashActive && (
          <div className="flex flex-col items-center text-center px-4 animate-in zoom-in-50 duration-500">
            {isDenied ? (
              <ShieldAlert size={120} className="text-white mb-6 drop-shadow-2xl animate-pulse" />
            ) : (
              <ShieldCheck size={120} className="text-white mb-6 drop-shadow-2xl" />
            )}
            
            <h1 className="text-7xl md:text-9xl font-black uppercase tracking-tighter drop-shadow-2xl mb-4">
              {isDenied ? 'ACCESO DENEGADO' : '¡BIENVENIDO!'}
            </h1>
            
            <p className="text-4xl md:text-6xl font-bold text-white/90 uppercase tracking-widest bg-black/20 px-8 py-4 rounded-3xl backdrop-blur-md">
              {isDenied ? currentFlash.message : currentFlash.userName}
            </p>
            
            {!isDenied && (
              <div className="mt-8 text-2xl font-black text-white/50 tracking-[5px] uppercase">
                {currentFlash.ticketType || 'Acceso Autorizado'}
              </div>
            )}
            {isDenied && (
              <div className="mt-8 text-2xl font-black text-white/50 tracking-[5px] uppercase">
                {currentFlash.userName !== 'Desconocido' ? `Usuario: ${currentFlash.userName}` : 'Ticket Falso / No Encontrado'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- DASHBOARD STANDBY VIEW --- */}
      <div className="flex w-full h-full relative z-10">
        
        {/* Main Center Area (Logo / Status) */}
        <div className="flex-1 flex flex-col items-center justify-center relative p-10">
           {/* Glow Effects */}
          <div className="absolute top-[20%] right-[20%] w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute bottom-[20%] left-[20%] w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
            <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
          </div>
          
          <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
            
            {/* Contenedor del Escáner */}
            <div className="relative w-full aspect-square max-w-[450px] bg-black rounded-[40px] border-4 border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden mb-8 group">
              <div id="kiosk-reader" className="w-full h-full"></div>
              
              {/* Overlay láser naranja */}
              {isScanning && !scanLoading && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
                  <div className="w-64 h-64 border-2 border-primary-orange/50 rounded-3xl relative">
                    <div className="absolute inset-0 border-2 border-primary-orange rounded-3xl animate-pulse"></div>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-0.5 bg-primary-orange shadow-[0_0_20px_rgba(249,115,22,1)] animate-scan-line"></div>
                  </div>
                </div>
              )}

              {scanLoading && (
                <div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                   <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* Selector de cámara */}
            <div className="flex items-center gap-4 bg-white/5 backdrop-blur-md px-6 py-3 rounded-full border border-white/10">
               <div className="flex items-center gap-2 text-white/50">
                 <Camera size={18} />
                 <span className="text-sm font-bold uppercase tracking-widest">{cameras[currentCameraIndex]?.label || "Inicializando cámara..."}</span>
               </div>
               {cameras.length > 1 && (
                 <button 
                   onClick={switchCamera}
                   className="p-2 ml-2 bg-white/10 rounded-full hover:bg-white/20 text-white transition-colors active:scale-95"
                 >
                   <RefreshCw size={16} />
                 </button>
               )}
            </div>
            
          </div>
          
          <div className="absolute bottom-10 left-10 opacity-30 flex items-center gap-3">
             <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
             <p className="text-[10px] font-black uppercase tracking-[5px]">Tecnología PASSAR Deportes - Control de Accesos</p>
          </div>
          
          <style>{`
            @keyframes scan-line {
              0% { top: 0%; }
              100% { top: 100%; }
            }
            .animate-scan-line {
              animation: scan-line 2s linear infinite;
            }
          `}</style>
        </div>

        {/* Sidebar History */}
        <div className="w-[450px] bg-gray-900/50 backdrop-blur-xl border-l border-white/5 p-6 flex flex-col items-stretch z-20 shadow-2xl">
          <div className="flex items-center gap-3 mb-8 text-white/50 border-b border-white/10 pb-6">
            <History size={20} />
            <h3 className="text-sm font-black uppercase tracking-widest">Historial Reciente</h3>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
            {logs.map((log) => {
              const isLogDenied = log.status === 'denied';
              return (
                <div 
                  key={log._id} 
                  className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                    isLogDenied ? 'bg-red-950/20 border-red-500/20' : 'bg-white/5 border-white/5'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg ${
                    isLogDenied ? 'bg-red-500/20 text-red-500' : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                  }`}>
                    {isLogDenied ? <ShieldAlert size={20} /> : getInitials(log.userName)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate text-base">
                      {log.userName}
                    </p>
                    <p className={`text-xs font-semibold truncate mt-1 ${
                      isLogDenied ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {isLogDenied ? log.message : 'Acceso Autorizado'}
                    </p>
                  </div>

                  <div className="text-[10px] text-white/30 font-bold whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second:'2-digit' })}
                  </div>
                </div>
              );
            })}
            
            {logs.length === 0 && (
              <div className="text-center text-white/20 py-10 text-sm font-medium">
                Esperando escaneos...
              </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default LiveMatchAccessScreen;
